import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SettingsName } from '@app/class/SettingsName';
import { ICombinedPreferences } from '@app/interfaces/ICombinedPreferences';
import { SessionService } from '@app/services/session.service';
import { CountryCode, isValidPhoneNumber } from "libphonenumber-js/min";
import { ICountry } from './ICountry';
import { IPhoneNumberOutput } from './smsInput.interfaces';
import './sms-input.component.scss';
import { SmsInputService } from './sms-input.service';

@Component({
    selector: 'sms-input',
    template: require('./sms-input.component.html')
})
export class SmsInputComponent implements OnInit, OnChanges {
    @Input() input: string;
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;

    @Output() outputChange = new EventEmitter<IPhoneNumberOutput>();

    userOptions: ICombinedPreferences;
    countries: ICountry[] = [];
    selectedCountry: ICountry;
    selectedIsoCode: string;
    selectedDialCode: number;
    phoneNumber: string;
    isValid: boolean;

    constructor(
        private readonly sessionService: SessionService,
        private readonly smsInputService: SmsInputService
    ) { }

    ngOnInit(): void {
        this.loadUserOptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.countries?.length) {
            this.countries = this.smsInputService.getCountries();
            this.setDefaultCountry();
        }
        if (changes['input'] && changes['input'].firstChange) {
            this.setDefaultCountry();
        }
    }

    onCountryChanged(): void {
        this.selectCountryFromIsoCode(this.selectedIsoCode);

        this.sendOutput();
    }

    onPhoneNumberChanged(): void {
        if (this.dialCodeHadChanged()) {
            this.setDefaultCountry();
            return;
        }

        this.sendOutput();
    }

    clear(): void {
        this.input = null;
        this.onPhoneNumberChanged();
    }

    private dialCodeHadChanged(): boolean {
        const newDialCode = this.getCountryDialCodeFromPhoneNumber(this.input);

        return newDialCode && newDialCode !== this.selectedCountry?.dialCode;
    }

    private sendOutput(): void {
        const finalPhoneNumber: string = this.input || undefined

        const output: IPhoneNumberOutput = {
            input: this.input,
            internationalized: finalPhoneNumber,
            country: this.selectedCountry,
            isValid: finalPhoneNumber ? this.isInternationalPhoneNumberValid(finalPhoneNumber) : true,
            isPristine: true,
            isDirty: false
        };

        this.isValid = output?.isValid;
        this.input = output?.internationalized;
        this.outputChange.emit(output);
    }

    private loadUserOptions(): void {
        this.userOptions = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);
    }

    private setDefaultCountry(): void {
        if (!this.userOptions) {
            this.loadUserOptions();
        }

        const dialCode = this.getCountryDialCodeFromPhoneNumber(this.input);

        if (!this.input || !dialCode) {
            this.selectCountryFromCountryCode(this.userOptions?.CountryCode?.Value || "gb");
        }
        else {
            this.selectCountryFromDialCode(dialCode);
        }

        this.onCountryChanged();
    }

    private getCountryDialCodeFromPhoneNumber(phoneNumber: string): number {
        if (!phoneNumber || phoneNumber.indexOf("+") !== 0) {
            return null;
        }

        const phoneNumberWithoutPlus: string = phoneNumber.replace(/ /g, "").replace(/\+/g, "").trim();

        return this.countries.find(country => phoneNumberWithoutPlus.indexOf(country.dialCode.toString()) === 0)?.dialCode;
    }

    private selectCountryFromCountryCode(countryCode: string): void {
        this.selectedCountry = this.countries.find(x => x.countryCode.toLowerCase() === countryCode.toLowerCase());

        this.onCountrySelectionUpdated();
    }

    private selectCountryFromIsoCode(isoCode: string): void {
        if (!isoCode) {
            return;
        }
        this.selectedCountry = this.countries.find(x => x.isoCode.toLowerCase() === isoCode.toLowerCase());

        this.onCountrySelectionUpdated();
    }

    private selectCountryFromDialCode(dialCode: number): void {
        this.selectedCountry = this.countries.find(x => x.dialCode === dialCode);

        this.onCountrySelectionUpdated();
    }

    private onCountrySelectionUpdated(): void {
        this.selectedIsoCode = this.selectedCountry?.isoCode;
        this.selectedDialCode = this.selectedCountry?.dialCode;
    }

    private isInternationalPhoneNumberValid(phoneNumber: string): boolean {
        return isValidPhoneNumber(phoneNumber, {
            defaultCountry: this.selectedCountry?.countryCode.toUpperCase() as CountryCode,
            defaultCallingCode: this.selectedCountry?.dialCode + '',
        });
    }
}
